/**
 * Theo Service Layer
 *
 * Responsibilities:
 *  - Gather full user health context from Supabase (cycle, symptoms,
 *    hormones, goals, memories) — used as Claude's per-turn context.
 *  - Stream chat responses from /api/theo/chat (Server-Sent Events).
 *  - Parse the optional <theo_state_update> JSON suffix and persist goals/memories.
 *  - Persist user/assistant messages to `theo_conversations`.
 *  - Compute Data Confidence (high / medium / low) based on freshness.
 *
 * Phase A refactor: pattern-detection, phase-derivation and
 * confidence-scoring now live in `intelligenceService.js` as the
 * single source of truth. This file re-exports `calcConfidence` for
 * backward-compat and imports the pure helpers.
 */
import { supabase } from "./supabase";
import {
  computePhase,
  detectHormonePattern,
  normalizeHormones,
  calcConfidence,
  deriveAssessmentTags,
} from "./intelligenceService";

// Re-export for existing callers that imported from theoService
export { calcConfidence };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const daysAgo = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

// ─────────────────────────────────────────────────────────────
// Build the full context payload for a chat request.
// ─────────────────────────────────────────────────────────────
export const buildTheoContext = async (userId, userName = "") => {
  const ctx = {
    user_name: userName || null,
    goals: [], memories: [],
    cycle_day: null, cycle_phase: null, cycle_last_updated_days_ago: null,
    last_symptom_update_days_ago: null, recent_symptoms: [],
    last_hormone_upload_days_ago: null, hormone_values: {},
    pcos_pattern: null, confidence: "low",
    assessment: null, assessment_tags: [],
  };
  if (!userId) return ctx;

  // Fetch all signals in parallel — keep boot fast
  const [goalsRes, memoriesRes, cycleRes, symptomsRes, hormoneRes, profileRes] = await Promise.all([
    supabase.from("theo_goals").select("goal_text").eq("user_id", userId).eq("active", true),
    supabase.from("theo_memories").select("memory_text").eq("user_id", userId)
      .order("created_at", { ascending: false }).limit(8),
    supabase.from("user_cycle_data").select("*").eq("user_id", userId)
      .order("updated_at", { ascending: false }).limit(1).maybeSingle(),
    supabase.from("daily_checkins").select("*").eq("user_id", userId)
      .order("checkin_date", { ascending: false }).limit(14),
    supabase.from("hormone_reports").select("*").eq("user_id", userId)
      .order("created_at", { ascending: false }).limit(1).maybeSingle(),
    supabase.from("profiles").select("age, diagnosis_status, cycle_regularity, primary_goal")
      .eq("id", userId).maybeSingle(),
  ]);

  ctx.goals = (goalsRes.data || []).map(r => r.goal_text).filter(Boolean);
  ctx.memories = (memoriesRes.data || []).map(r => r.memory_text).filter(Boolean);

  // Diagnostic: confirm what was retrieved from theo_memories on every boot
  if (memoriesRes.error) {
    console.warn("[Theo:memory] LOAD FAILED:", memoriesRes.error.message);
  } else {
    console.log(`[Theo:memory] LOADED ${ctx.memories.length} memories for user ${userId.slice(0, 8)}…`,
      ctx.memories);
  }

  // Cycle
  const cycle = cycleRes?.data;
  if (cycle) {
    ctx.cycle_day = cycle.cycle_day ?? null;
    ctx.cycle_phase = computePhase(cycle.cycle_day, cycle.cycle_length || 28);
    ctx.cycle_last_updated_days_ago = daysAgo(cycle.updated_at || cycle.created_at);
  }

  // Symptoms — last 14 days, find most recent + average severity
  const symptoms = symptomsRes?.data || [];
  if (symptoms.length > 0) {
    ctx.last_symptom_update_days_ago = daysAgo(symptoms[0].checkin_date || symptoms[0].created_at);
    const recent7 = symptoms.slice(0, 7);
    const signals = [];
    const symptomKeys = ["fatigue", "acne", "mood", "hair_loss", "bloating", "pain_level"];
    symptomKeys.forEach((k) => {
      const vals = recent7.map((s) => s.symptoms?.[k] ?? s[k]).filter((v) => typeof v === "number");
      if (vals.length >= 2) {
        const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
        if (avg >= 2) signals.push(`${k.replace("_", " ")} avg ${avg.toFixed(1)}/3 last ${vals.length}d`);
      }
    });
    ctx.recent_symptoms = signals.slice(0, 4);
  }

  // Hormones
  const hormones = hormoneRes?.data;
  if (hormones) {
    ctx.last_hormone_upload_days_ago = daysAgo(hormones.created_at);
    const hv = normalizeHormones(hormones);
    // Strip nulls so context stays tight
    Object.keys(hv).forEach(k => { if (hv[k] == null) delete hv[k]; });
    ctx.hormone_values = hv;
    ctx.pcos_pattern = detectHormonePattern(hv).summary;
  }

  ctx.confidence = calcConfidence({
    symptomsDaysAgo: ctx.last_symptom_update_days_ago,
    hormonesDaysAgo: ctx.last_hormone_upload_days_ago,
    cycleDaysAgo: ctx.cycle_last_updated_days_ago,
  });

  // Assessment / onboarding — surfaces the user's declared PCOS profile so
  // Theo's opening lines match her lived experience, not just her latest logs.
  const profile = profileRes?.data;
  if (profile) {
    ctx.assessment = {
      age:              profile.age              || null,
      diagnosis_status: profile.diagnosis_status || null,
      cycle_regularity: profile.cycle_regularity || null,
      primary_goal:     profile.primary_goal     || null,
    };
    ctx.assessment_tags = deriveAssessmentTags(profile);
  }

  return ctx;
};

// ─────────────────────────────────────────────────────────────
// Conversation persistence — Supabase (cross-session) + sessionStorage (in-session)
//
// In-session resilience: even before the user runs the SQL setup for
// theo_conversations, navigating Theo ↔ Home ↔ Theo must preserve the chat.
// We mirror every save into sessionStorage so a remount can rehydrate.
// ─────────────────────────────────────────────────────────────
const sessionKey = (userId) => `theo_session_conv_${userId}`;

const readSessionConv = (userId) => {
  try {
    const raw = sessionStorage.getItem(sessionKey(userId));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !Array.isArray(obj.messages)) return null;
    return obj; // { id, messages, savedToDb }
  } catch (_) { return null; }
};

const writeSessionConv = (userId, conv) => {
  try {
    sessionStorage.setItem(sessionKey(userId), JSON.stringify(conv));
  } catch (_) { /* quota / private mode — non-fatal */ }
};

export const clearSessionConv = (userId) => {
  try { sessionStorage.removeItem(sessionKey(userId)); } catch (_) { /* ignore */ }
};

// ─────────────────────────────────────────────────────────────
// Memory architecture health check — probes all four Theo tables.
// Used by the UI to detect when persistence is degraded and to verify
// setup post-SQL run. Returns { ok, missing: [...], errors: {...} }.
// ─────────────────────────────────────────────────────────────
export const checkMemoryHealth = async (userId) => {
  if (!userId) return { ok: false, missing: ["unauthenticated"], errors: {}, phase4aReady: false };
  const tables = ["theo_conversations", "theo_memories", "theo_goals", "theo_checkins"];
  const missing = [];
  const errors = {};
  await Promise.all(tables.map(async (t) => {
    try {
      // Use a plain limit-1 SELECT (not head:true) so PostgREST returns the
      // proper PGRST205 error code body when the table is missing.
      const { error } = await supabase.from(t).select("user_id").limit(1);
      if (error) {
        const msg = error.message || "";
        if (
          error.code === "42P01" ||
          error.code === "PGRST205" ||
          /not found|does not exist|schema cache/i.test(msg)
        ) {
          missing.push(t);
        } else {
          errors[t] = msg;
        }
      }
    } catch (e) {
      errors[t] = e?.message || String(e);
    }
  }));

  // Phase 4A probe — `archived` column on conversations + notification_prefs table
  let phase4aReady = false;
  if (!missing.includes("theo_conversations")) {
    const [archProbe, prefsProbe] = await Promise.all([
      supabase.from("theo_conversations").select("archived").limit(1),
      supabase.from("theo_notification_prefs").select("user_id").limit(1),
    ]);
    const archOk = !archProbe.error || !/archived/i.test(archProbe.error.message || "");
    const prefsOk = !prefsProbe.error || !/not found|does not exist|schema cache/i.test(prefsProbe.error.message || "");
    phase4aReady = archOk && prefsOk;
    if (archOk) _archivedColumnPresent = true;
    else _archivedColumnPresent = false;
  }

  return { ok: missing.length === 0 && Object.keys(errors).length === 0, missing, errors, phase4aReady };
};

// Flag set to true the first time we detect the Theo tables are missing,
// so we don't spam the console on every navigation.
let _theoTablesMissingWarned = false;

export const getOrCreateConversation = async (userId) => {
  // 1. Try in-session cache first — instant rehydration on remount
  const cached = readSessionConv(userId);

  // 2. Try to load most-recent conversation from Supabase
  try {
    const { data: existing, error } = await supabase
      .from("theo_conversations")
      .select("*")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(1);

    if (error) {
      // 42P01 = relation does not exist; PGRST205 = table not found in schema
      if (error.code === "42P01" || error.code === "PGRST205" || /not found|does not exist/i.test(error.message || "")) {
        if (!_theoTablesMissingWarned) {
          console.warn(
            "[Theo] theo_conversations table missing — chat will persist in-session only. " +
            "Run /app/SUPABASE_THEO_SETUP.md SQL to enable cross-session memory."
          );
          _theoTablesMissingWarned = true;
        }
        // Fall back to session cache
        if (cached) return cached;
        return { id: null, messages: [], savedToDb: false };
      }
      throw error;
    }

    if (existing && existing.length > 0) {
      const conv = { ...existing[0], savedToDb: true };
      writeSessionConv(userId, conv);
      return conv;
    }

    // No existing conversation — create one
    const { data, error: insErr } = await supabase
      .from("theo_conversations")
      .insert({
        user_id: userId,
        title: "Conversation with Theo",
        messages: cached?.messages || [],
      })
      .select("*")
      .single();

    if (insErr) {
      console.warn("[Theo] Could not create conversation in DB; using session cache.", insErr);
      const conv = cached || { id: null, messages: [], savedToDb: false };
      writeSessionConv(userId, conv);
      return conv;
    }

    const conv = { ...data, savedToDb: true };
    writeSessionConv(userId, conv);
    return conv;
  } catch (e) {
    console.warn("[Theo] getOrCreateConversation fallback to session cache:", e?.message || e);
    return cached || { id: null, messages: [], savedToDb: false };
  }
};

export const saveConversation = async (conversationId, messages, userId) => {
  // ALWAYS mirror to session cache first — guarantees in-session continuity
  if (userId) {
    writeSessionConv(userId, {
      id: conversationId,
      messages,
      savedToDb: Boolean(conversationId),
    });
  }
  // Then try the DB write (best-effort)
  if (!conversationId) return;
  try {
    const { error } = await supabase
      .from("theo_conversations")
      .update({ messages, updated_at: new Date().toISOString() })
      .eq("id", conversationId);
    if (error) console.warn("[Theo] saveConversation DB write failed:", error.message);
  } catch (e) {
    console.warn("[Theo] saveConversation exception:", e?.message || e);
  }
};

// ─────────────────────────────────────────────────────────────
// Multi-conversation system (Phase 3A)
// ─────────────────────────────────────────────────────────────

// Tracks (per-tab) whether the Phase 4A `archived` column is known to be
// present. Lets us issue a tight single-shot query that works on both
// pre- and post-migration schemas without spamming 400 errors.
let _archivedColumnPresent = null; // null = unknown, true/false = probed

// List all conversations for the user, newest first. Light projection only —
// no message bodies — to keep the sidebar fast even with many threads.
//
// Schema-safe: after the first call probes whether the Phase 4A `archived`
// column exists, the result is cached for the rest of the session. Subsequent
// calls only request columns that exist — no 400s, no retries.
export const listConversations = async (userId, { includeArchived = false } = {}) => {
  if (!userId) return [];
  try {
    const cols = _archivedColumnPresent === false
      ? "id, title, updated_at, messages"
      : "id, title, updated_at, messages, archived";
    const { data, error } = await supabase
      .from("theo_conversations")
      .select(cols)
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(50);

    // If we tried to include `archived` and the column doesn't exist yet,
    // cache that fact and retry once with the legacy columns.
    if (error && /archived/i.test(error.message || "")) {
      _archivedColumnPresent = false;
      const retry = await supabase
        .from("theo_conversations")
        .select("id, title, updated_at, messages")
        .eq("user_id", userId)
        .order("updated_at", { ascending: false })
        .limit(50);
      if (retry.error) {
        console.warn("[Theo:conv] listConversations retry failed:", retry.error.message);
        return [];
      }
      return (retry.data || []).map(c => ({
        id: c.id,
        title: c.title || "New Conversation",
        updated_at: c.updated_at,
        msg_count: Array.isArray(c.messages) ? c.messages.length : 0,
        archived: false,
        preview: (Array.isArray(c.messages) ? c.messages : [])
          .find(m => m.role === "user")?.content?.slice(0, 60) || "",
      }));
    }
    if (error) {
      console.warn("[Theo:conv] listConversations failed:", error.message);
      return [];
    }

    // Success: archived column is present, cache for the session.
    if (_archivedColumnPresent === null) _archivedColumnPresent = true;
    const rows = (data || []).map(c => ({
      id: c.id,
      title: c.title || "New Conversation",
      updated_at: c.updated_at,
      msg_count: Array.isArray(c.messages) ? c.messages.length : 0,
      archived: !!c.archived,
      preview: (Array.isArray(c.messages) ? c.messages : [])
        .find(m => m.role === "user")?.content?.slice(0, 60) || "",
    }));
    return includeArchived ? rows : rows.filter(c => !c.archived);
  } catch (e) {
    console.warn("[Theo:conv] listConversations exception:", e?.message || e);
    return [];
  }
};

// Exposed for callers (banner) to know whether Phase 4A migration is needed.
export const isArchiveSchemaReady = () => _archivedColumnPresent === true;

// Load a single conversation by id (with full messages)
export const loadConversation = async (conversationId) => {
  if (!conversationId) return null;
  try {
    const { data, error } = await supabase
      .from("theo_conversations")
      .select("*")
      .eq("id", conversationId)
      .single();
    if (error) {
      console.warn("[Theo:conv] loadConversation failed:", error.message);
      return null;
    }
    return { ...data, savedToDb: true };
  } catch (e) { return null; }
};

// Create a brand-new empty conversation. Returns the new row.
export const createNewConversation = async (userId) => {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from("theo_conversations")
      .insert({ user_id: userId, title: "New Conversation", messages: [] })
      .select("*")
      .single();
    if (error) {
      console.warn("[Theo:conv] createNewConversation failed:", error.message);
      return { id: null, messages: [], title: "New Conversation", savedToDb: false };
    }
    console.log("[Theo:conv] CREATED new conversation", data.id);
    return { ...data, savedToDb: true };
  } catch (e) { return null; }
};

// Auto-derive a conversation title from the first user message.
// Light client-side heuristic — no extra LLM call needed.
export const deriveTitle = (firstUserMessage) => {
  if (!firstUserMessage) return "New Conversation";
  let t = String(firstUserMessage).trim();
  // Strip common preambles
  t = t.replace(/^(please|hey theo,?|hi theo,?|theo,?|can you|could you|i want to|i need to|tell me|explain|what is|how do|how can|why|when|where|who)\s+/i, "");
  // Capitalize first letter
  t = t.charAt(0).toUpperCase() + t.slice(1);
  // Trim trailing punctuation
  t = t.replace(/[?.!,;:]+$/, "");
  // Truncate at word boundary near 40 chars
  if (t.length > 42) {
    t = t.slice(0, 42);
    const sp = t.lastIndexOf(" ");
    if (sp > 18) t = t.slice(0, sp);
    t = t + "…";
  }
  return t || "New Conversation";
};

// Update conversation title in DB
export const setConversationTitle = async (conversationId, title) => {
  if (!conversationId || !title) return;
  try {
    await supabase.from("theo_conversations")
      .update({ title })
      .eq("id", conversationId);
  } catch (e) { /* ignore */ }
};

// Archive (soft-delete) a conversation. It disappears from the default
// sidebar list but rows remain in DB.
// Returns { ok: true } on success, { ok: false, needsMigration: true } if
// the Phase 4A `archived` column is missing, else { ok: false }.
export const archiveConversation = async (conversationId) => {
  if (!conversationId) return { ok: false };
  try {
    const { error } = await supabase
      .from("theo_conversations")
      .update({ archived: true })
      .eq("id", conversationId);
    if (error) {
      if (/archived/i.test(error.message || "")) {
        _archivedColumnPresent = false;
        console.warn("[Theo:conv] archive needs Phase 4A migration:", error.message);
        return { ok: false, needsMigration: true };
      }
      console.warn("[Theo:conv] archive failed:", error.message);
      return { ok: false };
    }
    console.log("[Theo:conv] ARCHIVED", conversationId);
    return { ok: true };
  } catch (e) { return { ok: false }; }
};

export const unarchiveConversation = async (conversationId) => {
  if (!conversationId) return { ok: false };
  try {
    const { error } = await supabase
      .from("theo_conversations")
      .update({ archived: false })
      .eq("id", conversationId);
    if (error) {
      if (/archived/i.test(error.message || "")) {
        return { ok: false, needsMigration: true };
      }
      return { ok: false };
    }
    return { ok: true };
  } catch (e) { return { ok: false }; }
};

// Notification preferences (Phase 4A — backend infra only)
export const getNotificationPrefs = async (userId) => {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .from("theo_notification_prefs")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();
    if (error) {
      if (/not found|does not exist|schema cache/i.test(error.message || "")) return { _setupRequired: true };
      console.warn("[Theo:notif] getPrefs failed:", error.message);
      return null;
    }
    return data || null;
  } catch (e) { return null; }
};

export const upsertNotificationPrefs = async (userId, patch) => {
  if (!userId) return false;
  try {
    const tz = patch.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    const { error } = await supabase
      .from("theo_notification_prefs")
      .upsert({
        user_id: userId,
        push_enabled: patch.push_enabled ?? false,
        daily_brief: patch.daily_brief ?? true,
        weekly_recap: patch.weekly_recap ?? true,
        send_hour_local: patch.send_hour_local ?? 8,
        timezone: tz,
        updated_at: new Date().toISOString(),
      }, { onConflict: "user_id" });
    if (error) { console.warn("[Theo:notif] upsertPrefs failed:", error.message); return false; }
    return true;
  } catch (e) { return false; }
};

// ─────────────────────────────────────────────────────────────
// Memory CRUD (Phase 3A — Memory Panel needs edit/delete)
// ─────────────────────────────────────────────────────────────
export const listMemories = async (userId) => {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from("theo_memories")
      .select("id, memory_text, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) { console.warn("[Theo:memory] list failed:", error.message); return []; }
    return data || [];
  } catch (e) { return []; }
};

export const deleteMemory = async (memoryId) => {
  if (!memoryId) return false;
  try {
    const { error } = await supabase.from("theo_memories").delete().eq("id", memoryId);
    if (error) { console.warn("[Theo:memory] delete failed:", error.message); return false; }
    console.log("[Theo:memory] DELETED", memoryId);
    return true;
  } catch (e) { return false; }
};

export const updateMemoryText = async (memoryId, newText) => {
  if (!memoryId || !newText) return false;
  try {
    const text = String(newText).trim().slice(0, 200);
    if (!text) return false;
    const { error } = await supabase
      .from("theo_memories")
      .update({ memory_text: text })
      .eq("id", memoryId);
    if (error) { console.warn("[Theo:memory] update failed:", error.message); return false; }
    return true;
  } catch (e) { return false; }
};

export const listActiveGoals = async (userId) => {
  if (!userId) return [];
  try {
    const { data, error } = await supabase
      .from("theo_goals")
      .select("id, goal_text, created_at")
      .eq("user_id", userId)
      .eq("active", true)
      .order("created_at", { ascending: false });
    if (error) return [];
    return data || [];
  } catch (e) { return []; }
};
const STATE_UPDATE_RE = /<theo_state_update>([\s\S]*?)<\/theo_state_update>/;

export const parseStateUpdate = (text) => {
  const m = text.match(STATE_UPDATE_RE);
  if (!m) {
    console.log("[Theo:memory] PARSE: no <theo_state_update> block in response");
    return { cleanText: text, update: null };
  }
  let update = null;
  try { update = JSON.parse(m[1]); } catch (e) {
    console.warn("[Theo:memory] PARSE ERROR: invalid JSON in state_update block:", e.message);
    update = null;
  }
  if (update) {
    console.log("[Theo:memory] PARSED state_update:", update);
  }
  const cleanText = text.replace(STATE_UPDATE_RE, "").trim();
  return { cleanText, update };
};

export const applyStateUpdate = async (userId, update) => {
  if (!userId || !update) return;
  try {
    // Goals
    if (Array.isArray(update.goals_added) && update.goals_added.length) {
      // Dedupe against existing active goals
      const { data: existingGoals } = await supabase
        .from("theo_goals")
        .select("goal_text")
        .eq("user_id", userId)
        .eq("active", true);
      const seen = new Set((existingGoals || []).map(g => g.goal_text?.toLowerCase().trim()).filter(Boolean));
      const rows = update.goals_added
        .filter((g) => typeof g === "string" && g.trim())
        .map((g) => g.slice(0, 80).trim())
        .filter((g) => !seen.has(g.toLowerCase()))
        .map((g) => ({ user_id: userId, goal_text: g, active: true }));
      if (rows.length) {
        const { error } = await supabase.from("theo_goals").insert(rows);
        if (error) console.warn("[Theo:memory] GOAL INSERT failed:", error.message);
        else console.log(`[Theo:memory] GOAL INSERT: ${rows.length} new goal(s)`, rows.map(r => r.goal_text));
      }
    }
    if (Array.isArray(update.goals_removed) && update.goals_removed.length) {
      await supabase
        .from("theo_goals")
        .update({ active: false })
        .eq("user_id", userId)
        .in("goal_text", update.goals_removed.map((g) => String(g).slice(0, 80)));
    }
    // Memories — DEDUPED against the most recent memories for this user.
    // Anti-duplicate guard at the JS layer in case the LLM re-emits a memory
    // we already store. Comparison is case-insensitive, whitespace-trimmed.
    if (Array.isArray(update.memories) && update.memories.length) {
      const { data: existingMems } = await supabase
        .from("theo_memories")
        .select("memory_text")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50);
      const seen = new Set(
        (existingMems || [])
          .map(m => m.memory_text?.toLowerCase().trim())
          .filter(Boolean)
      );
      const rows = update.memories
        .filter((m) => typeof m === "string" && m.trim())
        .map((m) => m.slice(0, 200).trim())
        .filter((m) => !seen.has(m.toLowerCase()))
        .map((m) => ({ user_id: userId, memory_text: m }));
      console.log(`[Theo:memory] INSERT ATTEMPT — emitted ${update.memories.length}, after dedup ${rows.length}`,
        { emitted: update.memories, willInsert: rows.map(r => r.memory_text) });
      if (rows.length) {
        const { error } = await supabase.from("theo_memories").insert(rows);
        if (error) {
          console.warn("[Theo:memory] INSERT FAILED:", error.message, error);
        } else {
          console.log(`[Theo:memory] INSERT OK — ${rows.length} new row(s) written to theo_memories`);
        }
      }
    }
  } catch (e) {
    console.warn("[Theo:memory] applyStateUpdate exception:", e?.message || e);
  }
};

// ─────────────────────────────────────────────────────────────
// Daily Brief — fetched once per day per user.
// Cached in sessionStorage by `${userId}_${YYYY-MM-DD}` so it doesn't
// regenerate on every navigation; the brief stays consistent through the day.
// ─────────────────────────────────────────────────────────────
const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const briefCacheKey = (userId) => `theo_daily_brief_${userId}_${todayKey()}`;

export const getDailyBrief = async (userId, context) => {
  if (!userId) return null;
  // 1. Check the cache — has Theo already greeted her today?
  try {
    const cached = sessionStorage.getItem(briefCacheKey(userId));
    if (cached) {
      const obj = JSON.parse(cached);
      if (obj && typeof obj.brief === "string" && obj.brief.length > 0) return obj.brief;
    }
  } catch (_) { /* ignore cache miss */ }

  // 2. Fetch a fresh brief from the backend
  try {
    const resp = await fetch(`${BACKEND_URL}/api/theo/daily-brief`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ context }),
    });
    if (!resp.ok) {
      console.warn("[Theo] daily brief HTTP", resp.status);
      return null;
    }
    const data = await resp.json();
    const brief = (data && data.brief) || null;
    if (brief) {
      try {
        sessionStorage.setItem(briefCacheKey(userId), JSON.stringify({ brief, ts: Date.now() }));
      } catch (_) { /* ignore storage quota */ }
    }
    return brief;
  } catch (e) {
    console.warn("[Theo] daily brief failed:", e?.message || e);
    return null;
  }
};

// ─────────────────────────────────────────────────────────────
// Streaming chat (Server-Sent Events)
// onChunk(text) called for every token delta.
// Returns the final assembled text (with <theo_state_update> stripped).
// ─────────────────────────────────────────────────────────────
export const streamTheoChat = async ({
  userId, sessionId, userMessage, history, context, onChunk, onDone,
}) => {
  const resp = await fetch(`${BACKEND_URL}/api/theo/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({
      session_id: sessionId,
      user_message: userMessage,
      history: (history || []).map((m) => ({ role: m.role, content: m.content })),
      context,
    }),
  });
  if (!resp.ok || !resp.body) {
    throw new Error(`Theo chat failed: HTTP ${resp.status}`);
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let assembled = "";

  // Read SSE-formatted stream
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE events end with \n\n
    const events = buffer.split("\n\n");
    buffer = events.pop() || "";
    for (const ev of events) {
      const line = ev.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      const data = line.slice(6);
      try {
        const payload = JSON.parse(data);
        if (payload.type === "delta" && payload.content) {
          assembled += payload.content;
          if (onChunk) onChunk(payload.content, assembled);
        } else if (payload.type === "error") {
          throw new Error(payload.message || "Stream error");
        } else if (payload.type === "done") {
          // signaled end — outer loop will exit naturally
        }
      } catch (e) {
        // ignore malformed events
      }
    }
  }

  const { cleanText, update } = parseStateUpdate(assembled);
  if (update) await applyStateUpdate(userId, update);
  if (onDone) onDone(cleanText, update);
  return cleanText;
};
